/**
 * Guards against the React + browser auto-translate crash.
 *
 * Chrome/Edge/Safari auto-translate rewrites the DOM by wrapping text nodes in
 * injected <font> elements. React keeps its own model of the tree and assumes
 * it is the only thing mutating the DOM, so when it later removes or re-orders
 * a node during reconciliation it calls `parent.removeChild(node)` /
 * `parent.insertBefore(node, ref)` trusting the node is still a direct child.
 * After translate has re-parented that node, those calls throw:
 *
 *   DOMException: NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * The throw happens in React's commit phase, unwinds the whole render, and
 * tears down the subtree — which on /generate and /create-post means the user
 * loses all in-progress input. This is the long-standing facebook/react#11538
 * issue; the accepted fix (also shipped on facebook.com) is to make the two
 * native methods React relies on no-op gracefully when the node was moved by
 * translate, instead of throwing. Translation keeps working; the orphaned
 * <font>-wrapped nodes get cleaned up when their real parent unmounts.
 *
 * Imported for its side effect at the very top of index.tsx, before React
 * renders, so the patched methods are in place for the first commit.
 *
 * Suppressing the throw is intentionally non-fatal, but we still want
 * visibility: every suppression is logged to PostHog (throttled, since one
 * reconcile can fire many) so we keep signal on where/how often translate
 * collides — without recreating the page-bricking crash.
 */
import posthog from "posthog-js";

// Throttle logging: a single React commit can trigger many suppressions, so
// collapse a burst into one event/second carrying the count.
let lastLoggedAt = 0;
let suppressedSinceLog = 0;

function logSuppression(method: "removeChild" | "insertBefore") {
  suppressedSinceLog += 1;
  const now = Date.now();
  if (now - lastLoggedAt < 1000) return;
  const count = suppressedSinceLog;
  lastLoggedAt = now;
  suppressedSinceLog = 0;
  try {
    posthog.capture("translate_dom_guard_suppressed", {
      method,
      count, // suppressions collapsed into this event since the last one
      translate_dom_guard: true,
      $current_url:
        typeof window !== "undefined" ? window.location.href : undefined,
    });
  } catch {
    // PostHog not initialized yet (or capture failed) — never let logging
    // re-introduce a throw in the DOM hot path.
  }
}

if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Translate re-parented this node; React thinks it's our child but it
      // isn't. Don't throw — log it and hand it back so the commit continues.
      logSuppression("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was relocated by translate; appending is the safe
      // equivalent rather than throwing on a stale reference.
      logSuppression("insertBefore");
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

export {};
